import React, { useRef, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Spin,
  Dropdown,
  Tag,
} from 'antd';
import {
  MoreOutlined,
  SyncOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { TransferSchema } from '../../services/transferService';
import { formatDate } from '../../utils/format';

const { Text } = Typography;

interface ManualPayoutListProps {
  transfers: TransferSchema[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  onViewDetails: (transfer: TransferSchema) => void;
  isSmallScreen?: boolean;
}

const ManualPayoutList: React.FC<ManualPayoutListProps> = ({
  transfers,
  loading,
  hasMore,
  onLoadMore,
  onRefresh,
  onViewDetails,
  isSmallScreen = false,
}) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (!observerTarget.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, onLoadMore]);

  const getPurposeText = (purpose: string) => {
    switch (purpose.toLowerCase()) {
      case 'disbursement':
        return 'Disbursement';
      case 'manual-disbursement':
        return 'Manual Disbursement';
      case 'wallet-payout':
        return 'Wallet Payout';
      case 'wallet-refund':
        return 'Wallet Refund';
      default:
        return purpose;
    }
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (reference: string) => (
        <Text copyable>{reference}</Text>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 130,
      render: (_, record: TransferSchema) => (
        `${record.currency} ${record.amount.toLocaleString()}`
      ),
    },
    {
      title: 'Recipient',
      key: 'recipient',
      width: 180,
      ellipsis: true,
      render: (_, record: TransferSchema) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '14px' }}>{record.recipient_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.recipient_identifier}
          </div>
        </div>
      ),
    },
    {
      title: 'Bank',
      dataIndex: 'recipient_bank',
      key: 'recipient_bank',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      width: 150,
      render: (purpose: string) => getPurposeText(purpose),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: () => (
        <Tag color="orange">Manual Payout</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 140,
      render: (timestamp: number) => formatDate(timestamp),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_, record: TransferSchema) => {
        const menuItems = [
          {
            key: 'view',
            label: 'View Full Manual Payout Details',
            icon: <EyeOutlined />,
            onClick: () => onViewDetails(record),
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Manual Payout
        </Typography.Title>
        <Button
          icon={<SyncOutlined />}
          onClick={onRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={transfers}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 1000 }}
        size={isSmallScreen ? 'small' : 'middle'}
      />
      
      {/* Infinite scroll target */}
      <div ref={observerTarget} style={{ height: '20px', margin: '20px 0' }}>
        {isSmallScreen && hasMore && (
          <div style={{ textAlign: 'center' }}>
            <Spin size="small" />
            <span style={{ marginLeft: 8 }}>Loading more transfers...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualPayoutList;
